import { useMutation, useQueryClient } from "@tanstack/react-query";
import fetcher from "../utils/fetcher";
import { toast } from "src/components/shadcn/useToast";

// Products that support partner sync
// Values must match EnumProductTypes from src/types/listing.ts
const SYNC_SUPPORTED_PRODUCTS = ["expired", "fsbo", "preforeclosure", "ns", "customupload", "distressed"];

// Maximum number of records that can be synced at once
export const MAX_PARTNER_SYNC_LIMIT = 500;

// Threshold above which we show a confirmation dialog before syncing
export const PARTNER_SYNC_CONFIRM_THRESHOLD = 100;

// Shared toast helpers for partner sync operations
export const partnerSyncToasts = {
  noListings: () => toast({
    title: "No listings to sync",
    description: "Select listings to sync to partners",
    variant: "destructive",
  }),
  tooMany: () => toast({
    title: "Too many listings selected",
    description: `You can sync a maximum of ${MAX_PARTNER_SYNC_LIMIT.toLocaleString()} listings at once. Please refine your selection.`,
    variant: "destructive",
  }),
  started: (partnerName: string, count?: number) => toast({
    title: "Sync started",
    description: count
      ? `Syncing ${count} listing${count > 1 ? "s" : ""} to ${partnerName}`
      : `Syncing to ${partnerName}`,
    variant: "success",
  }),
  failed: (partnerName: string) => toast({
    title: "Sync failed",
    description: `Failed to sync to ${partnerName}`,
    variant: "destructive",
  }),
};

export function isProductSyncSupported(product: string): boolean {
  return SYNC_SUPPORTED_PRODUCTS.includes(product.toLowerCase());
}

// Format partner name(s) for display in confirmation dialogs and toasts
// Returns formatted name and whether it's a single partner
export function formatPartnerNames(
  partners: { id: string; name: string }[],
  partnerId?: string
): { partnerName: string; isSinglePartner: boolean } {
  if (partnerId) {
    const partnerName = partners.find((p) => p.id === partnerId)?.name || partnerId;
    return { partnerName, isSinglePartner: true };
  }

  // List all partner names: "A, B, and C" or "A and B" or just "A"
  const names = partners.map((p) => p.name);
  const isSinglePartner = names.length === 1;

  let partnerName: string;
  if (names.length === 1) {
    partnerName = names[0];
  } else if (names.length === 2) {
    partnerName = `${names[0]} and ${names[1]}`;
  } else {
    partnerName = `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  }

  return { partnerName, isSinglePartner };
}

// Validate count before syncing - returns action to take
// 'proceed': sync immediately without confirmation
// 'confirm': show confirmation dialog
// 'blocked': validation failed (toast already shown)
export function validatePartnerSyncCount(count: number): "proceed" | "confirm" | "blocked" {
  if (count === 0) {
    partnerSyncToasts.noListings();
    return "blocked";
  }
  if (count > MAX_PARTNER_SYNC_LIMIT) {
    partnerSyncToasts.tooMany();
    return "blocked";
  }
  if (count <= PARTNER_SYNC_CONFIRM_THRESHOLD) {
    return "proceed";
  }
  return "confirm";
}

// Execute partner sync with standardized toast handling
export function executePartnerSync(
  syncToPartners: (params: SyncToPartnersParams, options: { onSuccess: () => void; onError: () => void; onSettled?: () => void }) => void,
  params: {
    product: string;
    recordIds: (number | string)[];
    partnerId?: string;
    partnerName: string;
    onSettled?: () => void;
  }
) {
  syncToPartners(
    { product: params.product, recordIds: params.recordIds, partner: params.partnerId },
    {
      onSuccess: () => partnerSyncToasts.started(params.partnerName, params.recordIds.length),
      onError: () => partnerSyncToasts.failed(params.partnerName),
      ...(params.onSettled && { onSettled: params.onSettled }),
    }
  );
}

interface SyncToPartnersParams {
  product: string;
  recordIds: (number | string)[];
  partner?: string; // Optional - omit to sync to all enabled partners
}

interface SyncToPartnersResponse {
  success: boolean;
  synced: number;
  errors?: string[];
}

export function useSyncToPartners() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ product, recordIds, partner }: SyncToPartnersParams): Promise<SyncToPartnersResponse> => {
      const response = await fetcher({
        url: "/api/partner/push",
        method: "POST",
        body: {
          product,
          record_ids: recordIds,
          ...(partner && { partner }),
        },
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate any partner-related queries if needed
      queryClient.invalidateQueries({ queryKey: ["getPartners"] });
    },
  });
}
